import { useNotificationStore, NotificationType } from '../stores/notificationStore';

/**
 * Hook for displaying toast notifications
 * 
 * @example
 * ```tsx
 * const { showSuccess, showError, showWarning, showInfo } = useNotifications();
 * 
 * // Simple success notification
 * showSuccess('File Uploaded', 'Your file was uploaded successfully!');
 * 
 * // Error notification with longer duration
 * showError('Upload Failed', 'Please try again later', { duration: 7000 });
 * 
 * // Non-dismissible notification
 * showWarning('Important', 'Please read carefully', { autoClose: false });
 * ```
 */
export const useNotifications = () => {
  const { addNotification } = useNotificationStore();

  const showNotification = (
    type: NotificationType,
    title: string,
    message?: string,
    options?: {
      duration?: number;
      autoClose?: boolean;
    }
  ) => {
    addNotification({
      type,
      title,
      message,
      ...options,
    });
  };

  const showSuccess = (title: string, message?: string, options?: { duration?: number; autoClose?: boolean }) => {
    showNotification('success', title, message, options);
  };

  const showError = (title: string, message?: string, options?: { duration?: number; autoClose?: boolean }) => {
    showNotification('error', title, message, options);
  };

  const showWarning = (title: string, message?: string, options?: { duration?: number; autoClose?: boolean }) => {
    showNotification('warning', title, message, options);
  };

  const showInfo = (title: string, message?: string, options?: { duration?: number; autoClose?: boolean }) => {
    showNotification('info', title, message, options);
  };

  return {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};